import type { ClientQuestion, LocaleCode } from '@fixit/bozp-content';
import { MultiChoice } from './multi-choice';
import { Numeric } from './numeric';
import { OrderingQuiz } from './ordering-quiz';
import { SingleChoice } from './single-choice';
import { TrueFalse } from './true-false';

/**
 * The canonical encoded answer per kind — exactly what the server grader
 * consumes: `single` → choice-id string; `truefalse` → boolean; `multi` →
 * string[]; `numeric` → number; `ordering` → item-id string[]. `undefined`
 * represents an unanswered widget (or a blank numeric input).
 */
export type QuizAnswer = string | string[] | number | boolean | undefined;

export interface QuizWidgetProps {
  question: ClientQuestion;
  value: QuizAnswer;
  onChange: (value: QuizAnswer) => void;
  locale?: LocaleCode;
  trueLabel?: string;
  falseLabel?: string;
}

/**
 * Render-only dispatch over a graded question's `kind`. Never holds `correct`;
 * never grades. Emits the canonical encoded answer the server grader expects.
 */
export function QuizWidget({
  question,
  value,
  onChange,
  locale = 'cs-CZ',
  trueLabel,
  falseLabel,
}: QuizWidgetProps) {
  switch (question.kind) {
    case 'single':
      return (
        <SingleChoice
          question={question}
          value={value as string | undefined}
          onChange={onChange}
          locale={locale}
        />
      );
    case 'truefalse':
      return (
        <TrueFalse
          question={question}
          value={value as boolean | undefined}
          onChange={onChange}
          locale={locale}
          trueLabel={trueLabel}
          falseLabel={falseLabel}
        />
      );
    case 'multi':
      return (
        <MultiChoice
          question={question}
          value={value as string[] | undefined}
          onChange={onChange}
          locale={locale}
        />
      );
    case 'numeric':
      return (
        <Numeric
          question={question}
          value={value as number | undefined}
          onChange={onChange}
          locale={locale}
        />
      );
    case 'ordering':
      return (
        <OrderingQuiz
          question={question}
          value={value as string[] | undefined}
          onChange={onChange}
          locale={locale}
        />
      );
    default:
      return <div data-quiz-kind="unknown" />;
  }
}
